import { ChevronLeft, ChevronRight, MessageSquareText, Search, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { renderMarkdown } from "@navibiz/chat-core/markdown";
import { fmt, when } from "../../lib/format";
import { useConversations, useTranscript } from "../../lib/queries";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Dialog } from "../../ui/Dialog";
import { EmptyState, ErrorState, PageSkeleton } from "../../ui/Feedback";
import { Select } from "../../ui/Form";
import table from "../../ui/Table.module.css";
import { useProjectContext } from "./ProjectLayout";
import styles from "./ConversationsTab.module.css";

const HANDOFF: Record<string, string> = { order: "siap pesan", unknown: "tak terjawab", human: "minta tim" };

export function ConversationsTab() {
  const { project } = useProjectContext();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState(params.get("q") ?? "");
  const list = useConversations(project.id, params);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) value ? next.set(key, value) : next.delete(key);
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  if (list.isPending) return <PageSkeleton />;
  if (list.isError) return <ErrorState error={list.error} />;
  const { conversations, total, page, pageSize } = list.data;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <Card
        title="Percakapan"
        description="Disimpan 90 hari, lalu dihapus otomatis (UU PDP). Ringkasan pemakaian tetap tersimpan."
        flush
        actions={<span className={styles.total}>{fmt.int(total)} percakapan</span>}
      >
        <div className={styles.filters}>
          <form
            className={styles.search}
            onSubmit={(event) => {
              event.preventDefault();
              update({ q: draft.trim() || null });
            }}
          >
            <Search aria-hidden />
            <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Cari isi percakapan…" aria-label="Cari isi percakapan" />
          </form>
          <Select value={params.get("filter") ?? ""} onChange={(event) => update({ filter: event.target.value || null })} aria-label="Saring" className={styles.select}>
            <option value="">Semua percakapan</option>
            <option value="handoff">Dialihkan ke WhatsApp</option>
            <option value="unknown">Tak terjawab</option>
          </Select>
          <Select value={params.get("channel") ?? ""} onChange={(event) => update({ channel: event.target.value || null })} aria-label="Kanal" className={styles.select}>
            <option value="all">Semua platform</option>
            <option value="">Widget + API</option>
            <option value="widget">Widget</option>
            <option value="api">API</option>
            <option value="playground">Playground</option>
            <option value="test">Uji terima</option>
          </Select>
        </div>

        {conversations.length === 0 ? (
          <EmptyState icon={<MessageSquareText aria-hidden />} title="Tidak ada percakapan yang cocok" />
        ) : (
          <div className={table.wrap} style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
            <table className={table.table}>
              <thead>
                <tr>
                  <th className={table.first}>Pertanyaan pertama</th>
                  <th>Hasil</th>
                  <th className={table.right}>Pesan</th>
                  <th className={table.right}>Biaya</th>
                  <th className={`${table.right} ${table.last}`}>Terakhir</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((c) => (
                  <tr key={c.id} className={table.clickable} tabIndex={0} onClick={() => setOpen(c.id)} onKeyDown={(event) => event.key === "Enter" && setOpen(c.id)}>
                    <td className={table.first}>
                      <div className={styles.question}>{c.firstQuestion ?? "—"}</div>
                      <div className={table.muted}>
                        {c.channel}
                        {c.locale ? ` · ${c.locale.toUpperCase()}` : ""}
                      </div>
                    </td>
                    <td>
                      <div className={styles.badges}>
                        {c.handoff && <Badge tone={c.handoff === "unknown" ? "warning" : "accent"}>WA · {HANDOFF[c.handoff]}</Badge>}
                        {!c.handoff && <span className={table.muted}>dijawab bot</span>}
                      </div>
                    </td>
                    <td className={table.right}>{c.messageCount / 2}</td>
                    <td className={table.right}>{fmt.rupiah(c.costMilli)}</td>
                    <td className={`${table.right} ${table.last} ${table.muted}`}>{when.ago(c.lastMessageAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.pager}>
          <span className={table.muted}>
            Halaman {page} dari {pages}
          </span>
          <Button size="sm" icon={<ChevronLeft aria-hidden />} disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} aria-label="Sebelumnya" />
          <Button size="sm" icon={<ChevronRight aria-hidden />} disabled={page >= pages} onClick={() => update({ page: String(page + 1) })} aria-label="Berikutnya" />
        </div>
      </Card>

      <Transcript projectId={project.id} conversationId={open} onClose={() => setOpen(null)} />
    </>
  );
}

function Transcript({ projectId, conversationId, onClose }: { projectId: string; conversationId: string | null; onClose: () => void }) {
  const transcript = useTranscript(projectId, conversationId);
  const data = transcript.data;
  const totalCost = data?.messages.reduce((sum, m) => sum + m.costMilli, 0) ?? 0;
  const totalTokens = data?.messages.reduce((sum, m) => sum + m.inputTokens + m.outputTokens + m.cacheReadTokens + m.cacheWriteTokens, 0) ?? 0;

  return (
    <Dialog
      open={!!conversationId}
      onClose={onClose}
      variant="drawer"
      title="Transkrip percakapan"
      description={data ? `${when.dateTime(data.conversation.startedAt)} · ${fmt.int(totalTokens)} token · ${fmt.rupiah(totalCost)}` : "Memuat…"}
    >
      {transcript.isPending ? (
        <PageSkeleton />
      ) : transcript.isError ? (
        <ErrorState error={transcript.error} />
      ) : (
        <div className={styles.transcript}>
          {data!.conversation.pageUrl && <p className={styles.page}>Halaman: {data!.conversation.pageUrl}</p>}
          {data!.messages.map((message) =>
            message.role === "user" ? (
              <div key={message.id} className={`${styles.msg} ${styles.user}`}>
                {message.content}
              </div>
            ) : (
              <div key={message.id} className={styles.botBlock}>
                <div className={`${styles.msg} ${styles.bot}`} dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }} />
                {message.links.length > 0 && (
                  <ul className={styles.links} aria-label="Rujukan yang ditawarkan">
                    {message.links.map((link) => (
                      <li key={link.id}>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.title}
                        </a>
                        {message.linkClicks.includes(link.id) && <Badge tone="good">diklik</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
                <div className={styles.msgMeta}>
                  <span className="num">
                    {fmt.int(message.inputTokens)} in · {fmt.int(message.outputTokens)} out
                    {message.cacheReadTokens ? ` · ${fmt.int(message.cacheReadTokens)} cache` : ""}
                  </span>
                  <span>{fmt.rupiah(message.costMilli)}</span>
                  {message.latencyMs && <span>{(message.latencyMs / 1000).toFixed(1)} dtk</span>}
                  {message.markers.map((marker, i) => (
                    <Badge key={i} tone="accent">
                      {marker.type === "lead" ? "form kontak (lama)" : `WA · ${HANDOFF[marker.reason ?? ""] ?? marker.reason}`}
                    </Badge>
                  ))}
                  {message.feedback === "up" && <ThumbsUp aria-label="Dinilai membantu" />}
                  {message.feedback === "down" && <ThumbsDown aria-label="Dinilai kurang tepat" />}
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </Dialog>
  );
}
